import React from 'react';

//@ts-ignore
import * as styles from './product-detail-masthead.module.css';
import { ContentPosition, ProductDetailProps, VerticalPosition } from './index';
import { getMultiChoice, getColor } from '../Global/DataUtils/index';
import { getImageData } from '../Global/DataUtils/gatsbyImageUtils';
import CustomHeadline from '../Global/CustomHeadline';
import CtaLink from '../Global/CtaLink';
import AnchorLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import ArtDirectedImage from '../Global/ArtDirectedImage';

const ProductDetailMasthead: React.FC<ProductDetailProps> = ({
    under_image_bar_show_hide,
    general_large___small_image_selection__large,
    general_large___small_image_selection__small,
    tablet_image,
    product_name_position,
    text_background_color,
    text_background_opacity,
    display_headline,
    display_sub_headline,
    under_image_bar_color,
    vertical_alignment,
    display_headline_tag,
    display_sub_headline_tag,
    description_copy,
    description_tag,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_window_action,
    cta_configuration__cta_location_anchor_point,
    kontentItemId,
}) => {
    const height =
        getMultiChoice(under_image_bar_show_hide) === 'show'
            ? `calc(100vh - 100px)`
            : `92vh`;

    const ctaWindowAction = getMultiChoice(
        cta_configuration__cta_window_action
    );
    const anchorPointLink = cta_configuration__cta_location_anchor_point?.value;

    const contentPosition = getMultiChoice(product_name_position);
    const verticalAlign = getMultiChoice(vertical_alignment);

    const horizontalClass =
        contentPosition === 'center'
            ? styles.horizontalCenter
            : contentPosition === 'right'
            ? styles.horizontalRight
            : styles.horizontalLeft;

    const verticalClass =
        verticalAlign === 'top'
            ? styles.verticalTop
            : verticalAlign === 'bottom'
            ? styles.verticalBottom
            : verticalAlign === 'center'
            ? styles.verticalCenter
            : '';

    const renderImages = () => {
        const largeImageData = getImageData(
            general_large___small_image_selection__large
        );
        const smallImageData = general_large___small_image_selection__small
            ?.value[0]
            ? getImageData(general_large___small_image_selection__small)
            : null;
        const tabletImageData = tablet_image?.value[0]
            ? getImageData(tablet_image)
            : null;

        if (!largeImageData?.image) return null;

        const sources = [];
        if (smallImageData?.image) {
            sources.push({
                url: smallImageData.image.url,
                media: '(max-width: 576px)',
                width: 720,
                height: 1080,
                quality: 95,
            });
        }
        if (tabletImageData?.image) {
            sources.push({
                url: tabletImageData.image.url,
                media: '(max-width: 1024px)',
                width: 1024,
                height: 1366,
                quality: 95,
            });
        } else if (smallImageData?.image) {
            sources.push({
                url: largeImageData.image.url,
                media: '(max-width: 1024px)',
                width: 1024,
                height: 1366,
                quality: 95,
            });
        }
        sources.push({
            url: largeImageData.image.url,
            media: smallImageData?.image
                ? '(min-width: 1025px)'
                : '(min-width: 1px)',
            width: 1920,
            height: 988,
            quality: 95,
        });

        return (
            <ArtDirectedImage
                sources={sources}
                fallback={{
                    url: largeImageData.image.url,
                    width: 1920,
                    height: 988,
                    quality: 95,
                }}
                alt={largeImageData.alt}
                className={styles.picture}
                style={{ overflow: 'visible' }}
                imgStyle={{ height: '100vh' }}
                loading="eager"
                fetchPriority="high"
                elementCodename="general_large___small_image_selection__large"
            />
        );
    };

    const textBackgroundColor = getColor(text_background_color);
    const underImageBarShowHide = getMultiChoice(under_image_bar_show_hide);

    return (
        <section
            className={styles.wrapper}
            style={{ height: height }}
            data-kontent-item-id={kontentItemId}
        >
            <div
                className={styles.contentContainer}
                style={{ height: '100vh' }}
            >
                {renderImages()}

                {textBackgroundColor && (
                    <div
                        className={styles.bgOverlay}
                        style={{
                            backgroundColor: textBackgroundColor,
                            opacity: text_background_opacity?.value / 100,
                        }}
                    ></div>
                )}
            </div>
            <div
                className={`${styles.innerContentContainer} ${horizontalClass}`}
            >
                <div className={`${styles.innerContent} ${verticalClass}`}>
                    <div
                        style={
                            !display_sub_headline?.value
                                ? { display: 'none' }
                                : undefined
                        }
                    >
                        <CustomHeadline
                            style={'h5'}
                            tag={
                                display_sub_headline_tag?.value[0]?.codename
                                    ? display_sub_headline_tag
                                    : {
                                          value: [
                                              {
                                                  codename: 'h2',
                                                  name: 'h2',
                                              },
                                          ],
                                      }
                            }
                            headline_value={
                                display_sub_headline || { value: '' }
                            }
                            alignment={{ value: [{ codename: 'left' }] }}
                            overrideStyles={{
                                marginLeft: '0.3rem',
                            }}
                            elementCodename="display_sub_headline"
                        />
                    </div>
                    <div
                        style={
                            !display_headline?.value
                                ? { display: 'none' }
                                : undefined
                        }
                    >
                        <CustomHeadline
                            style={'h1'}
                            tag={
                                display_headline_tag?.value[0]?.codename
                                    ? display_headline_tag
                                    : {
                                          value: [
                                              {
                                                  codename: 'h1',
                                                  name: 'h1',
                                              },
                                          ],
                                      }
                            }
                            headline_value={display_headline || { value: '' }}
                            alignment={{ value: [{ codename: 'left' }] }}
                            overrideStyles={{
                                margin: '0px',
                            }}
                            elementCodename="display_headline"
                        />
                    </div>
                    <div
                        className={styles.descriptionContainer}
                        style={
                            !description_copy?.value
                                ? { display: 'none' }
                                : undefined
                        }
                    >
                        <CustomHeadline
                            style={'h5'}
                            tag={
                                description_tag?.value[0]?.codename
                                    ? description_tag
                                    : {
                                          value: [
                                              {
                                                  codename: 'h2',
                                                  name: 'h2',
                                              },
                                          ],
                                      }
                            }
                            headline_value={description_copy || { value: '' }}
                            alignment={{ value: [{ codename: 'left' }] }}
                            overrideStyles={{
                                marginLeft: '0.25rem',
                                lineHeight: '1.4',
                            }}
                            elementCodename="description_copy"
                        />
                    </div>
                    {cta_configuration__cta_label?.value && (
                        <div className={`${styles.ctaContainer} `}>
                            {!cta_configuration__cta_location?.value?.length &&
                            anchorPointLink ? (
                                <AnchorLink
                                    targetId={anchorPointLink}
                                    visualStyle="outlineLight"
                                >
                                    {cta_configuration__cta_label?.value}
                                </AnchorLink>
                            ) : (
                                <CtaLink
                                    href={BuildLinkHref(
                                        cta_configuration__cta_location,
                                        cta_configuration__cta_location_anchor_point
                                    )}
                                    target={
                                        ctaWindowAction === 'new_window'
                                            ? '_blank'
                                            : undefined
                                    }
                                    visualStyle="outlineLight"
                                >
                                    {cta_configuration__cta_label?.value}
                                </CtaLink>
                            )}
                        </div>
                    )}
                </div>
                {underImageBarShowHide === 'show' && (
                    <div
                        className={styles.underImageBar}
                        style={{
                            //@ts-ignore
                            backgroundColor: getColor(under_image_bar_color),
                        }}
                    ></div>
                )}
            </div>
        </section>
    );
};

export default ProductDetailMasthead;
